// Narrow POS receipt for pos sales — React port of the receipt branch of
// helpers/PrintComponent.vue (and the JSON receipt built by SaleListComponent).

import { Fragment, type ReactNode } from 'react';

import { formatDate, formatDateTime, t } from '../../i18n';
import { asArray, asRecord, makeFormatters, metaString, str, toNumber, type AnyRecord } from './format';
import { useStoreSettings, type StoreSettings } from './storeSettings';

function plain(html: unknown): string {
  return str(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim();
}

export function ReceiptDocument({ record, settings: settingsOverride }: { record: AnyRecord; settings?: StoreSettings }) {
  const loaded = useStoreSettings();
  const settings = settingsOverride ?? loaded;
  const fmt = makeFormatters(settings);
  const location = asRecord(record.location) ?? {};
  const customer = asRecord(record.customer);
  const user = asRecord(record.user);
  const logo = str(location.logo) || str(settings.default_logo);
  const payments = asArray(record.payments);
  const totalPaid = payments.reduce((sum, payment) => sum + toNumber(asRecord(payment.pivot)?.amount ?? payment.amount), 0);
  const headers = [plain(settings.header), plain(location.header)].filter(Boolean);
  const footers = [plain(settings.footer), plain(location.footer)].filter(Boolean);

  const line = (key: string, name: ReactNode, price: ReactNode, qty: ReactNode, subtotal: ReactNode, sub = false) => (
    <tr key={key} className={sub ? 'vd-sub' : undefined}>
      <td>{name}</td>
      <td className="vd-right">{price}</td>
      <td className="vd-center">{qty}</td>
      <td className="vd-right">{subtotal}</td>
    </tr>
  );

  const rows = asArray(record.items).map((item, index) => {
    const inner = asRecord(item.item);
    const name = (
      <>
        {str(item.code ?? inner?.code)}
        <br />
        <strong>{str(item.name ?? inner?.name)}</strong>
        {inner?.alt_name ? <div>{str(inner.alt_name)}</div> : null}
        {item.comment ? <div>{str(item.comment)}</div> : null}
      </>
    );
    const out: ReactNode[] = [];
    const variations = asArray(item.variations);
    const portions = asArray(item.portions);
    if (variations.length) {
      out.push(
        <tr key={`h${index}`}>
          <td colSpan={4}>{name}</td>
        </tr>
      );
      variations.forEach((variation, vi) => {
        const pivot = asRecord(variation.pivot) ?? {};
        out.push(line(`v${index}-${vi}`, metaString(variation.meta), fmt.money(toNumber(pivot.net_price) + toNumber(pivot.tax_amount)), fmt.qty(pivot.quantity), fmt.money(pivot.total), true));
      });
    } else if (portions.length) {
      out.push(
        <tr key={`h${index}`}>
          <td colSpan={4}>{name}</td>
        </tr>
      );
      portions.forEach((portion, pi) => {
        const pivot = asRecord(portion.pivot) ?? {};
        const portionQty = toNumber(pivot.quantity);
        out.push(
          line(
            `p${index}-${pi}`,
            <>
              {t('doc.portion')}: <strong>{portion.name === 'regular' ? t('doc.regular') : str(portion.name)}</strong>
            </>,
            fmt.money(toNumber(pivot.price) - toNumber(pivot.discount_amount)),
            fmt.qty(portionQty),
            fmt.money(pivot.total)
          )
        );
        [...asArray(portion.portion_items), ...asArray(portion.essentials)].forEach((entry, ei) => {
          out.push(line(`pe${index}-${pi}-${ei}`, `${str(asRecord(entry.item)?.name)}${entry.meta ? ` (${metaString(entry.meta)})` : ''}`, '', fmt.qty(toNumber(entry.quantity) * portionQty), '', true));
        });
        const chosen = asArray(pivot.choosables);
        asArray(portion.choosables).forEach((group, gi) => {
          asArray(group.items).forEach((groupItem, gii) => {
            if (!chosen.find((choice) => str(choice.id) === str(group.id) && str(choice.item_id) === str(groupItem.item_id))) return;
            out.push(line(`pc${index}-${pi}-${gi}-${gii}`, `${str(group.name)}: ${str(asRecord(groupItem.item)?.name)}`, '', fmt.qty(toNumber(groupItem.quantity) * portionQty), '', true));
          });
        });
      });
    } else {
      out.push(line(`i${index}`, name, fmt.money(item.unit_price), fmt.qty(item.quantity), fmt.money(item.subtotal)));
    }
    asArray(item.modifier_options).forEach((modifier, mi) => {
      const pivot = asRecord(modifier.pivot) ?? {};
      out.push(
        line(
          `m${index}-${mi}`,
          <>
            {str(asRecord(modifier.modifier)?.title)}: {str(asRecord(modifier.item)?.name ?? modifier.name)}
          </>,
          fmt.money(toNumber(pivot.net_price) + toNumber(pivot.tax_amount)),
          fmt.qty(pivot.quantity),
          fmt.money(pivot.total),
          true
        )
      );
    });
    return <Fragment key={str(item.id) || index}>{out}</Fragment>;
  });

  const totals: Array<[string, string]> = [[t('doc.total'), fmt.money(record.total)]];
  if (toNumber(record.order_discount_amount)) totals.push([t('doc.orderDiscount'), fmt.money(record.order_discount_amount)]);
  totals.push([t('doc.taxAmount'), fmt.money(record.total_tax_amount)]);
  totals.push([t('doc.grandTotal'), fmt.money(record.grand_total)]);
  totals.push([t('doc.paid'), fmt.money(totalPaid)]);
  totals.push([t('doc.balanceDue'), fmt.money(toNumber(record.grand_total) - totalPaid)]);

  return (
    <div className="vd-doc vd-receipt">
      <div className="vd-center-block">
        {logo ? <img className="vd-logo" src={logo} alt="" /> : null}
        {settings.name ? <h3>{str(settings.name)}</h3> : null}
        <div>
          {t('doc.location')}
          <br />
          <strong>{str(location.name)}</strong>
        </div>
        {[location.address, location.state_name, location.country_name].some(Boolean) ? <div>{[location.address, location.state_name, location.country_name].map(str).filter(Boolean).join(' ')}</div> : null}
        {location.email || location.phone ? <div>{[location.email, location.phone].map(str).filter(Boolean).join(' ')}</div> : null}
        {headers.length ? <div style={{ marginTop: 8, whiteSpace: 'pre-line' }}>{headers.join('\n')}</div> : null}
      </div>
      <h3>{t('doc.receipt')}</h3>
      <div>
        {t('doc.id')}: {str(record.id)}
      </div>
      {record.orderId ? (
        <div>
          {t('doc.orderId')}: {str(record.orderId)}
        </div>
      ) : null}
      <div>
        {t('doc.reference')}: {str(record.reference)}
      </div>
      {record.orderNumber ? (
        <div>
          {t('doc.orderNumber')}: {str(record.orderNumber)}
        </div>
      ) : null}
      {record.ticketNumber ? (
        <div>
          {t('doc.ticketNumber')}: {str(record.ticketNumber)}
        </div>
      ) : null}
      {record.date ? (
        <div>
          {t('doc.date')}: {formatDate(record.date)}
        </div>
      ) : null}
      {user ? (
        <div>
          {t('doc.createdBy')}: {str(user.name)}
          {user.username ? ` (${str(user.username)})` : ''}
        </div>
      ) : null}
      {record.created_at ? (
        <div>
          {t('doc.createdAt')}: {formatDateTime(record.created_at)}
        </div>
      ) : null}
      <div>
        {t('doc.printedAt')}: {formatDateTime(new Date().toISOString())}
      </div>
      {customer ? (
        <div>
          {t('doc.customer')}: <strong>{str(customer.name)}</strong>
          {customer.company ? ` (${str(customer.company)})` : ''}
          {str(customer.id) !== str(settings.default_customer) ? (
            <div>{[customer.email, customer.phone].map(str).filter(Boolean).join(' ')}</div>
          ) : null}
        </div>
      ) : null}
      <table>
        <thead>
          <tr>
            <th className="vd-left">{t('doc.name')}</th>
            <th className="vd-right">{t('doc.price')}</th>
            <th className="vd-center">{t('doc.qty')}</th>
            <th className="vd-right">{t('doc.subtotal')}</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
      <table>
        <tbody>
          {totals.map(([label, value]) => (
            <tr key={label}>
              <th className="vd-left">{label}</th>
              <td className="vd-right vd-bold">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {footers.length ? <div className="vd-center-block" style={{ whiteSpace: 'pre-line' }}>{footers.join('\n')}</div> : null}
      <p className="vd-cgd">{t('doc.cgd')}</p>
    </div>
  );
}
